// src/components/BottomNav.js
import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { isFullAccess } from '../permissions';

const ICONS = {
  home: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  box:  "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
  deliver: "M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2L19 8M10 12v4m4-4v4",
  search: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  admin: null,
  logout: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
};

export default function BottomNav() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const path      = location.pathname;
  const { worker, logout } = useContext(AuthContext);
  const role = worker?.role || 'view_only';

  // Menus follow the permissions table (10/23); each role gets exactly one.
  const isAdmin   = isFullAccess(role);                                   // Power User, General Superintendent
  const isDriver  = role === 'driver';
  const isForeman = ['foreman', 'site_supervisor'].includes(role);
  const isPM      = ['project_manager', 'project_engineer', 'project_administrator'].includes(role);
  const isOffice  = ['accountant', 'safety_director'].includes(role);     // User access: scan, My Items, transfers
  const isField   = ['carpenter', 'laborer', 'view_only'].includes(role);

  const NavBtn = ({ label, to, iconPath, active }) => (
    <button className={`bottom-nav-item ${active ? 'active' : ''}`} onClick={() => navigate(to)}>
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
      </svg>
      {label}
    </button>
  );

  const AdminIcon = ({ active }) => (
    <button className={`bottom-nav-item ${active ? 'active' : ''}`} onClick={() => navigate('/admin')}>
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      Admin
    </button>
  );

  const ScanBtn = () => (
    <button className="scan-nav-btn" onClick={() => navigate('/scan')}>
      <div className="scan-circle">
        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8H3a2 2 0 00-2 2v4a2 2 0 002 2h2m16-8h2a2 2 0 012 2v4a2 2 0 01-2 2h-2m-12 0h4" />
        </svg>
      </div>
      <span>SCAN</span>
    </button>
  );

  const LogOutBtn = () => (
    <button className="bottom-nav-item" onClick={logout}>
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={ICONS.logout} />
      </svg>
      Log Out
    </button>
  );

  return (
    <nav className="bottom-nav">
      <NavBtn label="Home" to="/" iconPath={ICONS.home} active={path === '/'} />

      {/* Power user / admin: Home Scan Load Deliver Admin LogOut */}
      {isAdmin && <>
        <ScanBtn />
        <NavBtn label="Load"    to="/scan-multiple?mode=truck" iconPath={ICONS.box}     active={path === '/scan-multiple'} />
        <NavBtn label="Deliver" to="/deliver"    iconPath={ICONS.deliver} active={path === '/deliver'} />
        <AdminIcon active={path.startsWith('/admin')} />
        <LogOutBtn />
      </>}

      {/* Driver: Home Scan Load Deliver Search LogOut */}
      {isDriver && <>
        <ScanBtn />
        <NavBtn label="Load"    to="/scan-multiple?mode=truck" iconPath={ICONS.box}     active={path === '/scan-multiple'} />
        <NavBtn label="Deliver" to="/deliver"    iconPath={ICONS.deliver} active={path === '/deliver'} />
        <NavBtn label="Search"  to="/search"     iconPath={ICONS.search}  active={path === '/search'} />
        <LogOutBtn />
      </>}

      {/* Foreman / site supervisor */}
      {isForeman && <>
        <ScanBtn />
        <NavBtn label="Load"     to="/scan-multiple?mode=truck" iconPath={ICONS.box}     active={path === '/scan-multiple'} />
        <NavBtn label="Deliver"  to="/deliver"    iconPath={ICONS.deliver} active={path === '/deliver'} />
        <NavBtn label="My Items" to="/my-items"   iconPath={ICONS.box}     active={path === '/my-items'} />
        <NavBtn label="Search"   to="/search"     iconPath={ICONS.search}  active={path === '/search'} />
        <LogOutBtn />
      </>}

      {/* Project manager / engineer / administrator */}
      {isPM && <>
        <ScanBtn />
        <NavBtn label="Deliver"  to="/deliver"  iconPath={ICONS.deliver} active={path === '/deliver'} />
        <NavBtn label="My Items" to="/my-items" iconPath={ICONS.box}     active={path === '/my-items'} />
        <NavBtn label="Search"   to="/search"   iconPath={ICONS.search}  active={path === '/search'} />
        <LogOutBtn />
      </>}

      {/* Accountant / safety director */}
      {isOffice && <>
        <ScanBtn />
        <NavBtn label="My Items" to="/my-items" iconPath={ICONS.box}    active={path === '/my-items'} />
        <NavBtn label="Search"   to="/search"   iconPath={ICONS.search} active={path === '/search'} />
        <LogOutBtn />
      </>}

      {/* Field workers: carpenter / laborer / view_only */}
      {isField && <>
        <ScanBtn />
        <NavBtn label="My Items" to="/my-items" iconPath={ICONS.box} active={path === '/my-items'} />
        <LogOutBtn />
      </>}

    </nav>
  );
}